import { useEffect, useState } from "react";
import {
  Alert, AlertDescription, AlertIcon, Button, Flex, FormControl, FormHelperText, FormLabel, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Tag, Text, Textarea, useToast,
} from "@chakra-ui/react";
import {
  cancelFiscalInvoice, FiscalInvoice, FiscalInvoiceResult, getFiscalEmailPreview, sendCorrectionLetter, sendFiscalEmail,
} from "../../../services/fiscalService";
import { crm } from "../../../theme";
import { brl } from "./duimp";

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

/** Igual ao servidor (FiscalHandler.CleanEventText): quebra de linha e espaços repetidos viram um espaço. */
const clean = (t: string) => t.replace(/\s+/g, " ").trim();

const Errors = ({ errors }: { errors: string[] }) =>
  errors.length === 0 ? null : (
    <Alert status="error" borderRadius="10px" mb="12px" alignItems="flex-start">
      <AlertIcon />
      <AlertDescription fontSize="13px" whiteSpace="pre-line">{errors.join("\n")}</AlertDescription>
    </Alert>
  );

type Done = (result: FiscalInvoiceResult) => void;

/** Texto livre com limite (justificativa do cancelamento / texto da CC-e). */
const TextAction = ({ title, intro, label, min, max, confirmLabel, danger, call, onClose, onDone }: {
  title: string;
  intro: React.ReactNode;
  label: string;
  min: number;
  max: number;
  confirmLabel: string;
  danger?: boolean;
  call: (text: string) => Promise<FiscalInvoiceResult>;
  onClose: () => void;
  onDone: Done;
}) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const length = clean(text).length;
  const valid = length >= min && length <= max;

  const submit = async () => {
    setBusy(true);
    const result = await call(clean(text));
    setBusy(false);
    // Recusa da SEFAZ volta com a nota (histórico novo): a tela de trás atualiza, o diálogo fica com o erro
    if (!result.success) { setErrors(result.errors); if (result.invoice) onDone(result); return; }
    onDone(result);
    onClose();
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} size="lg" closeOnOverlayClick={!busy}>
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">{title}</ModalHeader>
        {!busy && <ModalCloseButton />}
        <ModalBody>
          <Errors errors={errors} />
          <Text fontSize="13px" color={crm.textMuted} mb="12px">{intro}</Text>
          <FormControl>
            <Label>{label}</Label>
            <Textarea size="sm" rows={4} maxLength={max + 50} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            <FormHelperText fontSize="12px" color={length > 0 && !valid ? "red.500" : crm.textMuted}>
              {length} / {max} caracteres (mínimo {min})
            </FormHelperText>
          </FormControl>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>Voltar</Button>
          <Button colorScheme={danger ? "red" : undefined} bg={danger ? undefined : crm.brand} color="white"
            _hover={danger ? undefined : { bg: crm.brandDark }} isDisabled={!valid} isLoading={busy} onClick={submit}>
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const CancelInvoiceModal = ({ nf, onClose, onDone }: { nf: FiscalInvoice; onClose: () => void; onDone: Done }) => (
  <TextAction
    title={`Cancelar NF-e nº ${nf.number}`}
    intro={
      <>
        {nf.issuerEnvironment === "producao" ? "Cancelamento em PRODUÇÃO, não tem volta. " : "Homologação (teste, sem valor fiscal). "}
        A SEFAZ só aceita dentro do prazo (em geral 24 horas da autorização) e se a mercadoria não circulou. Total {brl(nf.totals.total)}.
      </>
    }
    label="Justificativa (vai para a SEFAZ)"
    min={15}
    max={255}
    confirmLabel="Cancelar NF-e"
    danger
    call={(text) => cancelFiscalInvoice(nf.id, text)}
    onClose={onClose}
    onDone={onDone}
  />
);

export const CorrectionLetterModal = ({ nf, onClose, onDone }: { nf: FiscalInvoice; onClose: () => void; onDone: Done }) => (
  <TextAction
    title={`Carta de correção · NF-e nº ${nf.number}`}
    intro={
      <>
        Corrige dados como endereço, transportadora, pesos, volumes e textos. Não corrige valores, impostos, quantidades, CNPJ/CPF
        de emitente ou destinatário nem a data de emissão (aí é cancelar e emitir de novo). A carta nova substitui as anteriores:
        escreva todas as correções.
        {(nf.correctionLetters?.length ?? 0) > 0 && ` Esta nota já tem ${nf.correctionLetters!.length} carta(s).`}
      </>
    }
    label="Correção"
    min={15}
    max={1000}
    confirmLabel="Enviar carta de correção"
    call={(text) => sendCorrectionLetter(nf.id, text)}
    onClose={onClose}
    onDone={onDone}
  />
);

const splitEmails = (raw: string) => raw.split(/[;,\s]+/).map((e) => e.trim()).filter(Boolean);
const isEmail = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

export const InvoiceEmailModal = ({ nf, onClose, onDone }: { nf: FiscalInvoice; onClose: () => void; onDone: Done }) => {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    getFiscalEmailPreview(nf.id)
      .then((p) => {
        setTo(p.to.join(", "));
        setCc(p.cc.join(", "));
        setSubject(p.subject);
        setBody(p.body);
        setAttachments(p.attachments);
        setLoaded(true);
      })
      .catch(() => { toast({ status: "error", title: "Não foi possível montar o e-mail" }); onClose(); });
  }, [nf.id]);

  const toList = splitEmails(to);
  const ccList = splitEmails(cc);
  const invalid = [...toList, ...ccList].filter((e) => !isEmail(e));
  const valid = toList.length > 0 && invalid.length === 0 && subject.trim() !== "" && body.trim() !== "";

  const send = async () => {
    setBusy(true);
    const result = await sendFiscalEmail(nf.id, { to: toList, cc: ccList, subject: subject.trim(), body });
    setBusy(false);
    if (!result.success) { setErrors(result.errors); return; }
    toast({ status: "success", title: result.errors.length ? result.errors.join(" ") : `E-mail enviado para ${toList.join(", ")}` });
    onDone(result);
    onClose();
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} size="2xl" closeOnOverlayClick={!busy} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">Enviar NF-e nº {nf.number} por e-mail</ModalHeader>
        {!busy && <ModalCloseButton />}
        <ModalBody>
          {!loaded ? (
            <Flex justify="center" py="24px"><Spinner /></Flex>
          ) : (
            <>
              <Errors errors={errors} />
              <FormControl mb="10px">
                <Label>Para (separe por vírgula)</Label>
                <Input size="sm" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@empresa.com.br" />
              </FormControl>
              <FormControl mb="10px">
                <Label>Cópia</Label>
                <Input size="sm" value={cc} onChange={(e) => setCc(e.target.value)} />
                {invalid.length > 0 && <FormHelperText fontSize="12px" color="red.500">E-mail inválido: {invalid.join(", ")}</FormHelperText>}
              </FormControl>
              <FormControl mb="10px">
                <Label>Assunto</Label>
                <Input size="sm" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
              </FormControl>
              <FormControl mb="10px">
                <Label>Mensagem</Label>
                <Textarea size="sm" rows={9} maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)} />
              </FormControl>
              <Label>Anexos (baixados da Focus NFe no envio)</Label>
              <Flex gap="6px" wrap="wrap">
                {attachments.map((a) => <Tag key={a} size="sm">{a}</Tag>)}
              </Flex>
              <Text fontSize="12px" color={crm.textMuted} mt="10px">Sai de sistema@cvftrading.com.br; as respostas vão para o seu e-mail.</Text>
            </>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>Voltar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!loaded || !valid} isLoading={busy} onClick={send}>
            Enviar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
